import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { Toast as ToastPrimitive } from "radix-ui";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { appConfig } from "@/config/app";
import { cx } from "@/lib/cx";

import styles from "./Toast.module.css";

export type ToastTone = "info" | "pass" | "fail";

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
}

interface ToastEntry extends ToastOptions {
  id: number;
}

const ToastContext = createContext<((toast: ToastOptions) => void) | null>(null);

const ICONS = { info: Info, pass: CircleCheck, fail: CircleAlert } as const;

/** Short, non-blocking confirmations ("Project created"). Errors that need action belong in the page. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastEntry[]>([]);
  const show = useCallback((toast: ToastOptions) => {
    setToasts((current) => [...current, { ...toast, id: Date.now() + current.length }]);
  }, []);
  const remove = (id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  };
  const api = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider duration={appConfig.toastDurationMs} swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const tone = toast.tone ?? "info";
          const Icon = ICONS[tone];
          return (
            <ToastPrimitive.Root
              key={toast.id}
              className={cx(styles.toast, styles[tone], "animate-in")}
              onOpenChange={(open) => {
                if (!open) remove(toast.id);
              }}
            >
              <Icon aria-hidden="true" className={styles.icon} />
              <div className={styles.text}>
                <ToastPrimitive.Title className={styles.title}>{toast.title}</ToastPrimitive.Title>
                {toast.description !== undefined && (
                  <ToastPrimitive.Description className={styles.description}>
                    {toast.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              <ToastPrimitive.Close aria-label="Dismiss" className={styles.close}>
                <X aria-hidden="true" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className={styles.viewport} />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): (toast: ToastOptions) => void {
  const show = useContext(ToastContext);
  if (show === null) throw new Error("useToast needs <ToastProvider> above it.");
  return show;
}
